import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";

type Content = { settings: { contact?: { email?: string; mobile?: string; pickupAddress?: string } } };

const LAST_UPDATED = "August 7, 2026";

export function PrivacyPolicyPage() {
  const content = useQuery({ queryKey: ["content"], queryFn: () => api<Content>("/content") });
  const contact = content.data?.settings.contact;
  return <section className="container-page py-10 md:py-16">
    <Seo title="Privacy Policy" description="How Minis by Cass collects, uses, and protects your personal information." path="/privacy-policy" />
    <div className="mx-auto max-w-3xl">
      <h1 className="heading">Privacy Policy</h1>
      <p className="mt-4 text-sm text-muted">Last updated: {LAST_UPDATED}</p>
      <div className="prose-legal mt-10 grid max-w-[68ch] gap-8 leading-relaxed text-cocoa/80 [&_a]:font-bold [&_a]:text-cocoa [&_a]:underline [&_a]:decoration-cocoa/30 [&_a]:underline-offset-4">
        <p>Minis by Cass (“we”, “us”, “our”) makes and sells custom clay crafts and 3D-printed keepsakes. This policy explains what personal information we collect when you shop with us, why we collect it, and how it is handled.</p>

        <Section title="Information we collect">
          <p>When you place an order, we collect:</p>
          <ul>
            <li>Your name, email address, and mobile number (mobile is optional).</li>
            <li>A delivery address, if you choose shipping instead of pickup.</li>
            <li>Order details: the items, customizations, and any reference photos you upload for a personalized piece.</li>
            <li>A screenshot or photo of your payment proof, and the payment reference number if you provide one.</li>
          </ul>
          <p>Before an order is created, we send a one-time verification code to the email you provide. This confirms the address is real and reachable, and helps us keep spam and fake orders off the production queue.</p>
          <p>We also process standard technical information automatically, such as your IP address and request timing, only to keep the site secure and to apply reasonable limits against abuse (for example, limiting how many verification codes can be requested in a short time).</p>
        </Section>

        <Section title="How we use your information">
          <ul>
            <li>To create, verify, and fulfill your order, including production and delivery or pickup.</li>
            <li>To verify your email before an order is created, reducing spam and fraudulent orders.</li>
            <li>To contact you about your order status, payment verification, and any issues with a submitted proof.</li>
            <li>To respond to questions you send us through the site's help widget or by contacting us directly.</li>
          </ul>
          <p>We do not use your information for advertising, and we do not sell or rent your personal information to third parties.</p>
        </Section>

        <Section title="Cookies and local storage">
          <p>The public shop does not use tracking or advertising cookies. Your shopping cart is stored only in your own browser (local storage) and is never sent to us until you check out. A secure session cookie is used only for the admin/staff login area.</p>
        </Section>

        <Section title="How your information is stored">
          <p>Order and account data is stored in our database. Uploaded photos and payment proofs are kept in private storage and are only ever accessible through time-limited, signed links — they are not publicly listed or browsable. Admin passwords are stored using industry-standard one-way hashing; we never store passwords in plain text.</p>
        </Section>

        <Section title="Who can see your information">
          <p>Your order details are visible only to the people who need them to make and fulfill your piece: the shop owner and authorized production staff. We do not share your personal information with third parties for their own marketing purposes.</p>
        </Section>

        <Section title="How long we keep it">
          <p>We keep order records for as long as reasonably necessary for business, accounting, and customer-support purposes. If you'd like us to delete personal information that we are not otherwise required to keep, contact us and we will accommodate this where we can.</p>
        </Section>

        <Section title="Your choices">
          <p>You can ask us at any time to see, correct, or delete the personal information we hold about you, subject to what we need to keep for legitimate business or legal reasons (such as completed order records).</p>
        </Section>

        <Section title="Children's privacy">
          <p>Minis by Cass is not directed at children, and we do not knowingly collect personal information from children.</p>
        </Section>

        <Section title="Changes to this policy">
          <p>We may update this policy from time to time as the shop grows. The “Last updated” date at the top of this page will always reflect the latest version.</p>
        </Section>

        <Section title="Contact us">
          <p>Questions about this policy or your personal information? Reach out to us{contact?.email ? <> at <a className="font-bold text-clay-dark underline" href={`mailto:${contact.email}`}>{contact.email}</a></> : null}{contact?.mobile ? <> or <span className="font-bold text-ink">{contact.mobile}</span></> : null}, or use the help button on the site.</p>
        </Section>
      </div>
      <Link to="/" className="btn-secondary mt-10 inline-flex">Back to the shop</Link>
    </div>
  </section>;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return <div><h2 className="text-xl font-bold text-cocoa">{title}</h2><div className="mt-3 grid gap-3 leading-relaxed [&_ul]:grid [&_ul]:list-disc [&_ul]:gap-2 [&_ul]:pl-5">{children}</div></div>;
}
